import React, { useEffect, useState } from "react";
import { AuditStatus, AuditType, IAudit } from "../../types/audit.type";
import { api } from "../../api/api";
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function Audit() {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<IAudit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [type, setType] = useState<AuditType | undefined>(undefined);
  const [status, setStatus] = useState<AuditStatus | undefined>(undefined);
  const [selectedAudit, setSelectedAudit] = useState<IAudit | undefined>(
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [completeForm] = Form.useForm();
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchAudits = async () => {
    setLoading(true);
    try {
      const response = await api.get("/audit", {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          type,
          status,
        },
      });

      setAudits(response.data.audits);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      message.error("Ошибка получение ревизий");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudits();
  }, [page, pageSize, dateRange, type, status]);

  const handleSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      if (values.type === AuditType.CLIENT) {
        await api.post("/audit/client", values);
      } else {
        await api.post("/audit", values);
      }

      message.success("Ревизия успешно создана.");
    } catch (error) {
      console.error("Error saving audit:", error);
      message.error("Ошибка добавления ревизии.");
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchAudits();
      form.resetFields();
    }
  };

  const handleComplete = async (audit: IAudit) => {
    setConfirmLoading(true);
    try {
      if (audit.type === AuditType.CLIENT) {
        await api.post(`/audit/client/${audit._id}/complete`);
      } else {
        await api.post(`/audit/${audit._id}/complete`);
      }

      message.success("Ревизия успешно завершена.");
    } catch (error: any) {
      console.error("Error completing audit:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      fetchAudits();
      completeForm.resetFields();
    }
  };

  const columns = [
    {
      title: "Дата начала",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "Дата окончания",
      dataIndex: "endDate",
      key: "endDate",
      render: (text: string) => {
        return text ? text : "Н/Д";
      },
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Создал",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "Сумма до USD",
      dataIndex: "amountBefore",
      key: "amountBefore.USD",
      render: (amountBefore: { USD: number; TJS: number }) =>
        roundToThreeDecimals(amountBefore.USD),
    },
    {
      title: "Сумма до TJS",
      dataIndex: "amountBefore",
      key: "amountBefore.TJS",
      render: (amountBefore: { USD: number; TJS: number }) =>
        roundToThreeDecimals(amountBefore.TJS),
    },
    {
      title: "Сумма после USD",
      dataIndex: "amountAfter",
      key: "amountAfter.USD",
      render: (amountAfter: { USD: number; TJS: number }) =>
        roundToThreeDecimals(amountAfter.USD),
    },
    {
      title: "Сумма после TJS",
      dataIndex: "amountAfter",
      key: "amountAfter.TJS",
      render: (amountAfter: { USD: number; TJS: number }) =>
        roundToThreeDecimals(amountAfter.TJS),
    },
    {
      title: "Разница USD",
      dataIndex: "difference",
      key: "difference.USD",
      render: (difference: { USD: number; TJS: number }) =>
        roundToThreeDecimals(difference.USD),
    },
    {
      title: "Разница TJS",
      dataIndex: "difference",
      key: "difference.TJS",
      render: (difference: { USD: number; TJS: number }) =>
        roundToThreeDecimals(difference.TJS),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, audit: IAudit) => {
        const menu = (
          <Menu>
            <Menu.Item
              onClick={() => {
                const path =
                  audit.type === AuditType.CLIENT
                    ? `/audit/${audit._id}/client-items`
                    : `/audit/${audit._id}/items`;
                navigate(path);
              }}
            >
              Смотреть данные
            </Menu.Item>
            <Menu.Item>
              <Popconfirm
                title="Вы уверены, что хотите завершить?"
                onConfirm={() => handleComplete(audit)}
                okText="Да"
                cancelText="Нет"
              >
                Завершить
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];
  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Ревизии</Title>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />

        <Select
          placeholder="Фильтр по типу"
          onChange={(value) => setType(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(AuditType).map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>

        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(AuditStatus).map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Добавить
        </Button>
      </Space>

      <Table
        loading={loading}
        columns={columns}
        dataSource={audits}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавиь ревизию"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="type"
            label="Тип"
            rules={[{ required: true, message: "Выберите Тип" }]}
          >
            <Select placeholder="Выберите Тип">
              {Object.values(AuditType).map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
